import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenerativeAI } from "@google/generative-ai";

const apiKey = localStorage.getItem('GEMINI_API_KEY') || "";
const genAI = apiKey ? new GoogleGenerativeAI(apiKey) : null;
const model = genAI ? genAI.getGenerativeModel({ model: "gemini-1.5-flash" }) : null;

import { PSYCH_POOL } from '../data/psych_eval_db';

const DIALOGUE_INITIAL = [
    { speaker: 'Samantha', text: "Systems online. Hello, Commander. I am Samantha, your Strategic Adaptive Management & Tactical Health Assistant." },
    { speaker: 'Samantha', text: "The world outside is currently... peaceful. But my predictive models indicate a 99.4% probability of global escalation within the next few months." },
    { speaker: 'Samantha', text: "My purpose is to guide you through the transition. We need to build your unit and secure a sector before the grid goes dark." },
    { speaker: 'Samantha', text: "Let us begin the Enlistment Protocol. I need to assess the psychological and tactical profile of your unit." }
];

// Questions are now loaded from PSYCH_POOL in ../data/psych_eval_db.js
const injectContext = (text, member) => {
    if (!text) return "";
    const g = member.gender === 'F' ? 'she' : 'he';
    const G = member.gender === 'F' ? 'She' : 'He';
    const p = member.gender === 'F' ? 'her' : 'his';
    const P = member.gender === 'F' ? 'Her' : 'His';
    const o = member.gender === 'F' ? 'her' : 'him';
    
    return text
        .replace(/\{NAME\}/gi, member.name.toUpperCase())
        .replace(/\{g\}/g, g)
        .replace(/\{G\}/g, G)
        .replace(/\{p\}/g, p)
        .replace(/\{P\}/g, P)
        .replace(/\{o\}/g, o);
};

export default function SamanthaTerminal({ gameState, dispatch }) {
    const [lineIdx, setCurLineIdx] = useState(0);
    const [evalState, setEvalState] = useState('INTRO'); // INTRO, NAME, AGE, GENDER, Q1, Q2, Q3, CONFIRM
    const [sessionQuestions, setSessionQuestions] = useState([]);
    const [curMember, setCurMember] = useState({ name: '', age: '', gender: 'M', traits: [] });
    const [displayedText, setDisplayedText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const chatEndRef = useRef();

    // Store the full text of the current line for skipping
    const currentFullTextRef = useRef('');

    // AUTO-SCROLL: scrolls sentinel div at bottom of chat into view
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, displayedText, evalState]);

    // TYPEWRITER EFFECT
    useEffect(() => {
        let textToType = '';
        if (evalState === 'INTRO') {
            textToType = DIALOGUE_INITIAL[lineIdx].text;
        } else if (evalState === 'NAME') {
            textToType = "TACTICAL_TERMINAL_ESTABLISHED. Identification required. Please input unit designation (NAME).";
        } else if (evalState === 'AGE') {
            textToType = `NAME_CONFIRMED: ${curMember.name.toUpperCase()}. Input biological age to verify personnel data and resource allocation.`;
        } else if (evalState === 'GENDER') {
            textToType = "IDENTITY_VERIFICATION_IN_PROGRESS... Please define unit biological designation (GENDER) for medical and social synchronization.";
        } else if (evalState === 'Q1') {
            textToType = `PERSONNEL_AUDIT 01/03: ${injectContext(sessionQuestions[0]?.text, curMember) || "..."}`;
        } else if (evalState === 'Q2') {
            textToType = `PERSONNEL_AUDIT 02/03: ${injectContext(sessionQuestions[1]?.text, curMember) || "..."}`;
        } else if (evalState === 'Q3') {
            textToType = `PERSONNEL_AUDIT 03/03: ${injectContext(sessionQuestions[2]?.text, curMember) || "..."}`;
        } else if (evalState === 'CONFIRM') {
            textToType = `PROFILE_SYNC_COMPLETE: ${curMember.name.toUpperCase()} enlistment finalized. Add another unit member?`;
        }
        
        currentFullTextRef.current = textToType;
        setIsTyping(true);
        setDisplayedText('');
        
        let charIdx = 0;
        const interval = setInterval(() => {
            setDisplayedText(textToType.substring(0, charIdx + 1));
            charIdx++;
            if (charIdx >= textToType.length) {
                clearInterval(interval);
                setIsTyping(false);
            }
        }, 12);

        return () => clearInterval(interval);
    }, [lineIdx, evalState, curMember, sessionQuestions]);

    // RELIABLE AUTO-ADVANCE logic (Decoupled from Typing Effect)
    useEffect(() => {
        if (!isTyping && evalState === 'INTRO') {
            const timer = setTimeout(() => {
                setChatHistory(prev => [...prev, { speaker: 'Samantha', text: DIALOGUE_INITIAL[lineIdx].text }]);
                if (lineIdx < DIALOGUE_INITIAL.length - 1) {
                    setCurLineIdx(prev => prev + 1);
                } else {
                    setEvalState('NAME');
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [isTyping, evalState, lineIdx]);


    const handleChoice = (e, choice) => {
        if (e && e.preventDefault) e.preventDefault();
        if (isProcessing) return;
        setIsProcessing(true);
        setTimeout(() => setIsProcessing(false), 400);

        setChatHistory(prev => [...prev, { speaker: 'Samantha', text: displayedText }, { speaker: gameState.user, text: `> ${choice.text.toUpperCase()}` }]);
        setCurMember(prev => ({ ...prev, traits: [...prev.traits, choice.trait] }));
        
        if (evalState === 'Q1') setEvalState('Q2');
        else if (evalState === 'Q2') setEvalState('Q3');
        else if (evalState === 'Q3') setEvalState('CONFIRM');
    };

    const handleNameSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!userInput.trim() || isProcessing) return;

        const isDuplicate = gameState.family.some(m => m.name.toLowerCase() === userInput.trim().toLowerCase());
        if (isDuplicate) {
            setDisplayedText("ERROR: DESIGNATION_ALREADY_IN_USE. Each unit member must have a unique identifier.");
            return;
        }

        setIsProcessing(true);
        setTimeout(() => setIsProcessing(false), 400);

        setChatHistory(prev => [...prev, { speaker: 'Samantha', text: displayedText }, { speaker: gameState.user, text: `> NAME: ${userInput.trim().toUpperCase()}` }]);
        setCurMember(prev => ({ ...prev, name: userInput.trim() }));
        setUserInput('');
        setEvalState('AGE');
    };

    const handleAgeSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!userInput.trim() || isProcessing) return;

        const age = parseInt(userInput);
        if (isNaN(age) || age < 1 || age > 120) return;

        setIsProcessing(true);
        setTimeout(() => setIsProcessing(false), 400);

        let category = 'ADULT';
        if (age < 5) category = 'INFANT';
        else if (age < 13) category = 'CHILD';
        else if (age < 20) category = 'TEEN';
        else if (age >= 60) category = 'ELDER';

        setChatHistory(prev => [...prev, { speaker: 'Samantha', text: displayedText }, { speaker: gameState.user, text: `> AGE: ${age}` }]);
        setCurMember(prev => ({ ...prev, age, ageCategory: category }));
        setUserInput('');
        setEvalState('GENDER');
    };

    const handleGenderSubmit = (e, gender) => {
        if (e && e.preventDefault) e.preventDefault();
        if (isProcessing) return;

        setIsProcessing(true);
        setTimeout(() => setIsProcessing(false), 400);

        const genLabel = gender === 'M' ? 'BIOLOGICAL_MALE' : 'BIOLOGICAL_FEMALE';
        setChatHistory(prev => [...prev, { speaker: 'Samantha', text: displayedText }, { speaker: gameState.user, text: `> DESIGNATION: ${genLabel}` }]);

        // Load questions based on age AND gender
        const isCommander = gameState.family.length === 0;
        let poolCategory = 'UNIT';
        if (isCommander) poolCategory = 'COMMANDER';
        else if (curMember.age < 5) poolCategory = 'INFANT';
        else if (curMember.age < 13) poolCategory = 'CHILD';
        else if (curMember.age < 20) poolCategory = 'TEEN';
        else if (curMember.age >= 60) poolCategory = 'ELDER';

        const poolObj = PSYCH_POOL.find(p => p.category === poolCategory);
        const fullPool = poolObj?.questions || [];
        const genderMatchedPool = fullPool.filter(q => q.gender === 'ANY' || q.gender === gender);
        const usedIds = gameState.usedQuestionIds || [];
        const availablePool = genderMatchedPool.filter(q => !usedIds.includes(q.id));
        const poolToUse = availablePool.length >= 3 ? availablePool : genderMatchedPool;
        const shuffled = [...poolToUse].sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, 3);

        setSessionQuestions(selected);
        setCurMember(prev => ({ ...prev, gender }));
        setEvalState('Q1');
    };

    const handleConfirm = (e, addAnother) => {
        if (e && e.preventDefault) e.preventDefault();
        if (evalState !== 'CONFIRM' || isProcessing) return;
        setIsProcessing(true);
        setTimeout(() => setIsProcessing(false), 400);
        
        const finishedMember = { ...curMember };
        
        // Relationship Logic: The second member is often the spouse/partner if adult.
        if (gameState.family.length === 1 && (finishedMember.ageCategory === 'ADULT' || finishedMember.ageCategory === 'ELDER')) {
            finishedMember.role = 'SPOUSE';
        }

        // Record used questions
        const questionIds = sessionQuestions.map(q => q.id);
        dispatch({ type: 'UPDATE_USED_QUESTIONS', payload: questionIds });
        
        dispatch({ type: 'ADD_MEMBER', payload: finishedMember });
        
        setIsTyping(false);
        if (addAnother && gameState.family.length < 5) { // Max 6 members (user + 5 family)
            setChatHistory(prev => [...prev, { speaker: 'Samantha', text: displayedText }, { speaker: gameState.user, text: "> AFFIRMATIVE. ADD_UNIT." }]);
            setCurMember({ name: '', age: '', gender: 'M', traits: [] });
            setSessionQuestions([]);
            setEvalState('NAME');
        } else {
            setChatHistory(prev => [...prev, { speaker: 'Samantha', text: displayedText }, { speaker: gameState.user, text: "> NEGATIVE. UNIT_COMPLETE." }]);
            dispatch({ type: 'FINISH_CHAT' });
        }
    };

    const handleRestorePrevious = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!gameState.savedSetup || isProcessing) return;
        setIsProcessing(true);
        setTimeout(() => setIsProcessing(false), 400);

        setChatHistory(prev => [...prev, { speaker: 'Samantha', text: "RESTORE_SEQUENCE_INITIATED. Loading previous personnel records..." }]);
        dispatch({ type: 'RESTORE_SAVED_UNIT' });
    };

    return (
        <div className="min-h-0 flex-grow w-full flex flex-col items-center justify-center p-2 md:p-4 bg-[#1c1917] relative">
            <div className="glass w-full max-w-3xl h-full lg:h-[650px] rounded-2xl border border-[#f59e0b]/30 flex flex-col overflow-hidden shadow-2xl relative">
                <div className="absolute inset-0 bg-gradient-to-b from-[#f59e0b]/5 to-transparent pointer-events-none"></div>
                
                {/* Header */}
                <div className="bg-[#292524] border-b border-[#f59e0b]/20 p-4 flex justify-between items-center z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_#10b981]"></div>
                        <span className="text-xs mono font-black uppercase tracking-widest text-emerald-500">Secure Link: SAMANTHA_v4.5</span>
                    </div>
                    <div className="flex gap-4 items-center">
                        {gameState.savedSetup && (
                            <button 
                                onClick={handleRestorePrevious}
                                className="text-[9px] mono bg-[#f59e0b]/10 text-[#f59e0b] border border-[#f59e0b]/30 px-2 py-1 rounded hover:bg-[#f59e0b] hover:text-black transition-all font-black"
                            >
                                [ RESTORE_PREVIOUS_UNIT ]
                            </button>
                        )}
                        <span className="text-[10px] mono text-[#f59e0b]/40 uppercase tracking-tighter">Unit_Size: {gameState.family.length}/6</span>
                        <span className="text-[10px] mono text-[#f59e0b]/40 uppercase tracking-tighter">Latency: 14ms</span>
                    </div>
                </div>

                {/* Chat Area */}
                <div className="flex-grow p-8 overflow-y-auto space-y-8 font-mono text-sm md:text-base scroll-smooth relative">
                    {chatHistory.map((d, i) => (
                        <div key={i} className={`flex flex-col animate-in fade-in duration-500 ${d.speaker === 'Samantha' ? 'items-start' : 'items-center'}`}>
                            <span className={`text-[10px] font-black uppercase mb-2 ${d.speaker === 'Samantha' ? 'text-[#f59e0b]' : 'text-sky-400'}`}>
                                [ {d.speaker} ]
                            </span>
                            <div className={`p-4 rounded-xl max-w-[85%] leading-relaxed ${d.speaker === 'Samantha' ? 'bg-[#292524] text-[#f59e0b]/80 border border-[#f59e0b]/10' : 'bg-sky-900/20 text-sky-400 border border-sky-500/20'}`}>
                                {d.text}
                            </div>
                        </div>
                    ))}
                    
                    {/* Active Line */}
                    {displayedText && (
                        <div className="flex flex-col items-start pb-4">
                            <span className="text-[10px] font-black uppercase mb-2 text-[#f59e0b]">
                                [ Samantha ]
                            </span>
                            <div className="p-4 rounded-xl max-w-[85%] leading-relaxed bg-[#292524] text-[#f59e0b] border border-l-4 border-[#f59e0b] shadow-[0_0_20px_rgba(245,158,11,0.05)]">
                                {displayedText}
                                {isTyping && <span className="inline-block w-2 h-4 bg-[#f59e0b] ml-1 animate-pulse"></span>}
                            </div>
                        </div>
                    )}

                    {/* Scroll sentinel — always at bottom of chat content */}
                    <div ref={chatEndRef} />

                    {/* Inputs based on state - only hidden by typing for questions */}
                    <div className="animate-in slide-in-from-bottom-4 duration-500">
                        {!isTyping && (
                            <>
                                {evalState === 'NAME' && (
                                    <div className="flex flex-col items-center py-4 bg-black/40 p-6 rounded-2xl border border-white/5">
                                        <input 
                                            type="text" 
                                            value={userInput}
                                            onChange={(e) => setUserInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleNameSubmit(e);
                                            }}
                                            placeholder="INPUT_IDENTITY..."
                                            className="bg-[#1c1917] border-b-2 border-[#f59e0b] text-[#f59e0b] p-4 w-full max-w-lg text-center outline-none mono text-lg tracking-widest uppercase focus:bg-[#292524] transition-all"
                                            autoFocus
                                        />
                                        <button
                                            onClick={(e) => handleNameSubmit(e)}
                                            disabled={isProcessing}
                                            className="mt-6 bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b] px-8 py-2 rounded text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-[#f59e0b] hover:text-black transition-all disabled:opacity-30"
                                        >
                                            VERIFY_NAME
                                        </button>
                                    </div>
                                )}

                                {evalState === 'AGE' && (
                                    <div className="flex flex-col items-center py-4 bg-black/40 p-6 rounded-2xl border border-white/5">
                                        <input 
                                            type="number" 
                                            value={userInput}
                                            onChange={(e) => setUserInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleAgeSubmit(e);
                                            }}
                                            placeholder="INPUT_AGE..."
                                            className="bg-[#1c1917] border-b-2 border-[#f59e0b] text-[#f59e0b] p-4 w-full max-w-lg text-center outline-none mono text-lg tracking-widest uppercase focus:bg-[#292524] transition-all"
                                            autoFocus
                                        />
                                        <button
                                            onClick={(e) => handleAgeSubmit(e)}
                                            disabled={isProcessing}
                                            className="mt-6 bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b] px-8 py-2 rounded text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-[#f59e0b] hover:text-black transition-all disabled:opacity-30"
                                        >
                                            SYNC_AGE_DATA
                                        </button>
                                    </div>
                                )}

                                {evalState === 'GENDER' && (
                                    <div className="flex flex-col items-center gap-6 py-4 bg-black/40 p-8 rounded-3xl border border-[#f59e0b]/10 backdrop-blur-xl">
                                        <div className="text-[10px] mono text-[#f59e0b]/40 tracking-[0.3em] uppercase mb-2">Biometric_Pattern_Detected</div>
                                        <div className="flex gap-6 justify-center w-full">
                                            <button 
                                                onClick={(e) => handleGenderSubmit(e, 'M')}
                                                disabled={isProcessing}
                                                className="flex-1 bg-[#292524] border-2 border-[#f59e0b]/20 p-8 rounded-2xl text-[#f59e0b] hover:bg-sky-500/10 hover:border-sky-500 hover:text-sky-400 transition-all mono text-sm flex flex-col items-center gap-4 group shadow-2xl relative overflow-hidden"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-t from-sky-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                                <span className="text-5xl group-hover:scale-110 transition-transform filter drop-shadow-[0_0_10px_rgba(14,165,233,0.5)]">♂</span>
                                                <div className="flex flex-col items-center">
                                                    <span className="font-black tracking-[0.2em] uppercase">MALE</span>
                                                    <span className="text-[8px] opacity-40 mt-1">IDENTITY_VERIFICATION: BIOLOGICAL_MALE</span>
                                                </div>
                                            </button>
                                            <button 
                                                onClick={(e) => handleGenderSubmit(e, 'F')}
                                                disabled={isProcessing}
                                                className="flex-1 bg-[#292524] border-2 border-[#f59e0b]/20 p-8 rounded-2xl text-[#f59e0b] hover:bg-rose-500/10 hover:border-rose-500 hover:text-rose-400 transition-all mono text-sm flex flex-col items-center gap-4 group shadow-2xl relative overflow-hidden"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-t from-rose-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                                <span className="text-5xl group-hover:scale-110 transition-transform filter drop-shadow-[0_0_10px_rgba(244,63,94,0.5)]">♀</span>
                                                <div className="flex flex-col items-center">
                                                    <span className="font-black tracking-[0.2em] uppercase">FEMALE</span>
                                                    <span className="text-[8px] opacity-40 mt-1">IDENTITY_VERIFICATION: BIOLOGICAL_FEMALE</span>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {(evalState === 'Q1' || evalState === 'Q2' || evalState === 'Q3') && (
                                    <div className="grid grid-cols-1 gap-3 max-w-lg mx-auto">
                                        {(() => {
                                            const idx = evalState === 'Q1' ? 0 : evalState === 'Q2' ? 1 : 2;
                                            const q = sessionQuestions[idx];
                                            if (!q) return null;
                                            return q.choices.map((c, i) => (
                                                <button 
                                                    key={i}
                                                    onClick={(e) => handleChoice(e, c)}
                                                    className="bg-[#292524] border border-[#f59e0b]/20 p-4 rounded-xl text-[#f59e0b] text-left hover:bg-[#f59e0b] hover:text-black transition-all mono text-xs flex justify-between group"
                                                >
                                                    <span>{injectContext(c.text, curMember)}</span>
                                                    <span className="opacity-0 group-hover:opacity-100 transition-opacity">SELECT →</span>
                                                </button>
                                            ));
                                        })()}
                                    </div>
                                )}

                                {evalState === 'CONFIRM' && (
                                    <div className="flex gap-4 justify-center">
                                        <button 
                                            onClick={(e) => handleConfirm(e, true)}
                                            className="bg-[#f59e0b] text-black px-10 py-4 rounded-xl font-black text-sm uppercase tracking-widest hover:bg-white transition-all shadow-[0_0_30px_rgba(245,158,11,0.2)]"
                                        >
                                            YES / ADD_UNIT
                                        </button>
                                        <button 
                                            onClick={(e) => handleConfirm(e, false)}
                                            className="bg-[#292524] text-[#f59e0b] border border-[#f59e0b] px-10 py-4 rounded-xl font-black text-sm uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all shadow-[0_0_30px_rgba(245,158,11,0.1)]"
                                        >
                                            NO / UNIT_READY
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Footer / Action (Auto-Logic is now continuous) */}
                <div className="p-4 bg-[#1c1917] border-t border-[#f59e0b]/10 flex justify-center items-center h-16 text-[9px] mono opacity-30 uppercase tracking-[0.3em]">
                    Tactical_Link_Status: SECURE | Streaming...
                </div>
            </div>
        </div>
    );
}